import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FirearmLicence, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const FIREARMS_ROLES = ['FIREARMS_REGISTRAR'];

/**
 * Police — firearm licences (M05). m05-police has no GET /firearms/:id
 * detail endpoint (list only returns the full row, decision/issue/revoke
 * take just the id) — the detail modal below is fed straight from the row
 * already in hand rather than an extra fetch that doesn't exist.
 */
export function Firearms() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => FIREARMS_ROLES.includes(r)) : false;
  const [selected, setSelected] = useState<FirearmLicence | null>(null);

  const listQ = useApiQuery<Page<FirearmLicence>>('/api/v1/police/firearms?limit=50');

  return (
    <div>
      <PageHeader title="Firearms Licences" subtitle="Pistol/rifle/shotgun licence applications and the issued register (M05)." />

      {!isRegistrar ? <RoleNotice roles={FIREARMS_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No firearm licences on file" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Type</th>
                <th className="table-head">Description</th>
                <th className="table-head">Serial no.</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(f)}>
                  <td className="table-cell font-mono text-xs font-medium">{f.nin}</td>
                  <td className="table-cell">{f.licenceType}</td>
                  <td className="table-cell text-xs text-slate-500">{f.firearmDescription}</td>
                  <td className="table-cell font-mono text-xs">{f.serialNo}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected ? (
        <FirearmDetailModal
          licence={selected}
          canAct={isRegistrar}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FirearmDetailModal({
  licence,
  canAct,
  onClose,
  onChanged,
}: {
  licence: FirearmLicence;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [notes, setNotes] = useState('');
  const [showRevoke, setShowRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${licence.licenceType} — ${licence.nin}`} onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Detail label="State" value={<StatusBadge status={licence.state} />} />
          <Detail label="Type" value={licence.licenceType} />
          <Detail label="Serial no." value={licence.serialNo} mono />
          <Detail label="Certificate serial" value={licence.certSerial ?? '—'} mono />
          <Detail label="Issued" value={licence.issuedAt ? formatDateTime(licence.issuedAt) : '—'} />
          <Detail label="Expires" value={licence.expiresAt ? formatDateTime(licence.expiresAt) : '—'} />
        </div>
        <Detail label="Firearm description" value={licence.firearmDescription} />
        {licence.reviewNotes ? <Detail label="Review notes" value={`${licence.reviewNotes} (${licence.reviewedBy})`} /> : null}

        {(licence.state === 'APPLIED' || licence.state === 'UNDER_REVIEW') ? (
          canAct ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="flex gap-2">
                <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                  Approve
                </button>
                <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                  Reject
                </button>
              </div>
              {decision ? (
                <>
                  <Field label="Notes (required)">
                    <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </Field>
                  <button
                    className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                    disabled={busy || notes.trim().length < 3}
                    onClick={() => call(() => api.post(`/api/v1/police/firearms/${licence.id}/decision`, { decision, notes: notes.trim() }))}
                  >
                    {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                  </button>
                </>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={FIREARMS_ROLES} />
          )
        ) : null}

        {licence.state === 'APPROVED' ? (
          canAct ? (
            <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/police/firearms/${licence.id}/issue`))}>
              {busy ? 'Issuing…' : 'Issue licence'}
            </button>
          ) : (
            <RoleNotice roles={FIREARMS_ROLES} />
          )
        ) : null}

        {licence.state === 'ISSUED' ? (
          canAct ? (
            !showRevoke ? (
              <button className="btn-danger" onClick={() => setShowRevoke(true)}>
                Revoke licence
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <Field label="Revocation reason">
                  <input className="field-input" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
                </Field>
                <button
                  className="btn-danger"
                  disabled={busy}
                  onClick={() => call(() => api.post(`/api/v1/police/firearms/${licence.id}/revoke`, { reason: revokeReason || 'Revoked by registrar' }))}
                >
                  {busy ? 'Revoking…' : 'Confirm revoke'}
                </button>
              </div>
            )
          ) : (
            <RoleNotice roles={FIREARMS_ROLES} />
          )
        ) : null}

        <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
          Filed {formatDateTime(licence.createdAt)} · last updated {formatDateTime(licence.updatedAt)}
        </div>
      </div>
    </Modal>
  );
}
